// GPGPUParticles.jsx
import { useRef, useEffect, useMemo } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Center, useGLTF } from '@react-three/drei'
import { Clock, Float32BufferAttribute, } from 'three'
import { useControls } from 'leva'
import { Perf } from 'r3f-perf'
import gsap from 'gsap'


/* --- Helper Functions --- */
import setupGPUComputation from './helpers/setupGPUComputation.jsx'
import generateParticlesData from './helpers/generateParticlesData.jsx'
import createParticlesGeometry from './helpers/createParticlesGeometry.jsx'
import createParticlesMaterial from './helpers/createParticlesMaterial.jsx'

import useTabStore from '../../stores/useActiveTab.jsx'
import useMainContentTab from '../../stores/useMainContentTab.jsx'
import useActiveModel from '../../stores/useActiveModel.jsx'

export default function GPGPUParticles() {
  // Separate tween refs for each animated parameter.
  const influenceTweenRef = useRef()
  const frequencyTweenRef = useRef()
  const strengthTweenRef = useRef()

  const { activeTab } = useTabStore()
  const { mainContentTab } = useMainContentTab()
  const { activeModel, morphTransition, setActiveModel, startMorphTransition, endMorphTransition } = useActiveModel()

  // Leva controls for GPU simulation and particle size
  const { ffInfluence, ffStrength, ffFrequency } = useControls('GPGPU Controls', {
    ffInfluence: { value: 0.6, min: 0, max: 5, step: 0.001 },
    ffStrength: { value: 1.5, min: 0, max: 10, step: 0.001 },
    ffFrequency: { value: 0.03, min: 0, max: 1, step: 0.001 },
  })
  const { levaProgress } = useControls({
    levaProgress: {
      value: 0,
      min: 0,
      max: 1,
      step: 0.01,
    }
  })

  const { gl, size } = useThree()
  const pixelRatio = gl.getPixelRatio()

  // Load GLTF model.
  let particles = null

  const gltf = useGLTF('/model/customSphere-blueText-baked-compressedTexture-12k.glb')
  useGLTF.preload('/model/customSphere-blueText-baked-compressedTexture-12k.glb')

  particles = {}
  particles.index = 0

  const positions = gltf.scene.children.map(child => child.geometry.attributes.position)

  particles.maxCount = 0
  for (const position of positions) {
    if (position.count > particles.maxCount)
      particles.maxCount = position.count
  }

  particles.positions = []
  for (const position of positions) {

    const originalArray = position.array
    const newArray = new Float32Array(particles.maxCount * 3)

    for (let i = 0; i < particles.maxCount; i++) {

      const i3 = i * 3

      if (i3 < originalArray.length) {
        newArray[i3 + 0] = originalArray[i3 + 0]
        newArray[i3 + 1] = originalArray[i3 + 1]
        newArray[i3 + 2] = originalArray[i3 + 2]
      } else {
        const randomIndex = Math.floor(position.count * Math.random()) * 3
        newArray[i3 + 0] = originalArray[randomIndex + 0]
        newArray[i3 + 1] = originalArray[randomIndex + 1]
        newArray[i3 + 2] = originalArray[randomIndex + 2]
      }
    }
    particles.positions.push(new Float32BufferAttribute(newArray, 3))
  }

  const targetPositionsArray = activeModel === 'thankyou' ? particles.positions[1].array : particles.positions[0].array

  const baseGeometry = useMemo(() => gltf.scene.children[0].geometry, [gltf])
  //const baseGeometry = useMemo(() => {
  //  return activeModel === 'thankyou'
  //    ? gltf.scene.children[1].geometry
  //    : gltf.scene.children[0].geometry;
  //}, [gltf, activeModel]);

  const count = baseGeometry.attributes.position.count

  // Set up GPU computation.
  const gpuComputationRef = useRef(null)
  const particlesVariableRef = useRef(null)
  useEffect(() => {
    const { gpu, variable } = setupGPUComputation(gl, baseGeometry, count);
    gpuComputationRef.current = gpu;
    particlesVariableRef.current = variable;
  }, [gl, baseGeometry, count]);

  // Create particles data and geometry.
  const particlesData = useMemo(() => generateParticlesData(count), [count])
  const particlesGeometry = useMemo(
    () => createParticlesGeometry(baseGeometry, particlesData, count, targetPositionsArray),
    [baseGeometry, particlesData, count]
  )

  useEffect(() => {
    if (particlesGeometry && particlesGeometry.attributes.aPositionTarget) {
      particlesGeometry.attributes.aPositionTarget.array.set(targetPositionsArray)
      particlesGeometry.attributes.aPositionTarget.needsUpdate = true
    }
  }, [activeModel, targetPositionsArray, particlesGeometry])

  // Create the ShaderMaterial for particles.
  const particlesMaterial = useMemo(
    () => createParticlesMaterial(size, pixelRatio),
    [size, pixelRatio]
  )

  // Update resolution when the canvas size changes.
  useEffect(() => {
    particlesMaterial.uniforms.uResolution.value.set(
      size.width * pixelRatio,
      size.height * pixelRatio
    )
  }, [size, pixelRatio, particlesMaterial])

  // Animation loop: update simulation time and pass computed texture.
  const clockRef = useRef(new Clock())
  const prevTimeRef = useRef(0)
  useFrame(() => {
    const elapsedTime = clockRef.current.getElapsedTime()
    const deltaTime = elapsedTime - prevTimeRef.current
    prevTimeRef.current = elapsedTime

    if (particlesVariableRef.current && gpuComputationRef.current) {

      // Update time uniforms.
      particlesVariableRef.current.material.uniforms.uTime.value = elapsedTime
      particlesVariableRef.current.material.uniforms.uDeltaTime.value = deltaTime

      // Compute simulation step.
      gpuComputationRef.current.compute()

      // Pass the computed texture to the particle material.
      particlesMaterial.uniforms.uParticlesTexture.value = gpuComputationRef.current.getCurrentRenderTarget(particlesVariableRef.current).texture
    }
  })

  /*
   * Animate parameters based on activeTab.
   */

  useEffect(() => {
    if (particlesVariableRef.current) {
      // Mappings for activeTab
      const activeTabInfluenceValues = {
        Home: 0.4,
        About: 0.6,
        Projects: 0.5,
        Skills: 0.8,
        Contact: 0.8,
      };
      // Mappings for mainContentTab (if you want these to override activeTab)
      const mainContentInfluenceValues = {
        StartScreen: 0.6,
        ThankyouScreen: 3.0,
      };

      // Choose targetInfluence based on mainContentTab first,
      // or fallback to activeTab mapping
      const targetInfluence =
        mainContentInfluenceValues[mainContentTab] !== undefined
          ? mainContentInfluenceValues[mainContentTab]
          : activeTabInfluenceValues[activeTab] || ffInfluence;

      if (influenceTweenRef.current) influenceTweenRef.current.kill();
      influenceTweenRef.current = gsap.to(
        particlesVariableRef.current.material.uniforms.uFlowFieldInfluence,
        {
          value: targetInfluence,
          duration: 1.5,
          ease: 'sine.inOut',
          overwrite: true,
        }
      );
    }
  }, [activeTab, mainContentTab, ffInfluence]);


  useEffect(() => {
    if (particlesVariableRef.current) {
      const activeTabStrengthValues = {
        Home: 15.0,
        About: 5.0,
        Projects: 10,
        Skills: 8,
        Contact: 40,
      };
      const mainContentStrengthValues = {
        StartScreen: 2.0,
        ThankyouScreen: 20.0,
      };

      const targetStrength =
        mainContentStrengthValues[mainContentTab] !== undefined
          ? mainContentStrengthValues[mainContentTab]
          : activeTabStrengthValues[activeTab] || ffStrength;

      if (strengthTweenRef.current) strengthTweenRef.current.kill();
      strengthTweenRef.current = gsap.to(
        particlesVariableRef.current.material.uniforms.uFlowFieldStrength,
        {
          value: targetStrength,
          duration: 1.5,
          ease: 'sine.inOut',
          overwrite: true,
        }
      );
    }
  }, [activeTab, mainContentTab, ffStrength]);

  useEffect(() => {
    if (particlesVariableRef.current) {
      const activeTabFrequencyValues = {
        Home: 0.5,
        About: 0.2,
        Projects: 0.09,
        Skills: 0.04,
        Contact: 0.01,
      };
      const mainContentFrequencyValues = {
        StartScreen: 0.07,
        ThankyouScreen: 0.1,
      };

      const targetFrequency =
        mainContentFrequencyValues[mainContentTab] !== undefined
          ? mainContentFrequencyValues[mainContentTab]
          : activeTabFrequencyValues[activeTab] || ffFrequency;

      if (frequencyTweenRef.current) frequencyTweenRef.current.kill();
      frequencyTweenRef.current = gsap.to(
        particlesVariableRef.current.material.uniforms.uFlowFieldFrequency,
        {
          value: targetFrequency,
          duration: 1.5,
          ease: 'sine.inOut',
          overwrite: true,
        }
      );
    }
  }, [activeTab, mainContentTab, ffFrequency]);

  useEffect(() => {
    if (morphTransition) {
      gsap.to(particlesMaterial.uniforms.uProgress, {
        value: levaProgress,
        duration: 2,
        ease: 'sine.inOut',
        onComplete: () => {
          setActiveModel("earth")
          endMorphTransition()
        }
      })
    }
  }, [levaProgress, morphTransition, particlesMaterial, setActiveModel, endMorphTransition])

  useEffect(() => {
    const targetProgress = mainContentTab === "ThankyouScreen" ? 1 : 0
    gsap.to(particlesMaterial.uniforms.uProgress, {
      value: targetProgress,
      duration: 2,
      ease: 'sine.inOut'
    })
  }, [mainContentTab, particlesMaterial])

  return (
    <>
      <Center>
        <points geometry={particlesGeometry} material={particlesMaterial} />
      </Center>
    </>
  )
}
